declare global {
	interface Window {
		grecaptcha?: {
			ready: (cb: () => void) => void;
			execute: (captchaKey: string, opts: { action: 'submit' }) => Promise<string | undefined>;
		};
	}
}

function resolveSiteKey(siteKey?: string): string | undefined {
	return siteKey ?? import.meta.env.VITE_GOOGLE_RECAPTCHA_SITE_KEY;
}

export type GenerateCaptchaTokenError = 'INVALID_CAPTCHA' | 'CAPTCHA_NOT_LOADED' | 'INVALID_CAPTCHA_KEY_OR_ORIGIN';

type GenerateCaptchaTokenResult = { data: string; error: null } | { data: null; error: GenerateCaptchaTokenError };

const RECAPTCHA_SCRIPT_ID = 'recaptcha-script';
// A deferred script on a slow connection takes seconds; 300 ms was the spurious "not loaded" (TEC-3418).
const RECAPTCHA_READY_TIMEOUT_MS = 10_000;

/** Shared by the form-mount warm-up and the submit, so neither injects its own script. */
let loadState: { key: string; promise: Promise<boolean> } | undefined;

/** Rendered with the same key `execute` gets, so the two cannot mismatch. */
export function loadRecaptcha(siteKey?: string): Promise<boolean> {
	const key = resolveSiteKey(siteKey);
	if (typeof window === 'undefined' || typeof document === 'undefined' || !key) return Promise.resolve(false);
	if (loadState && loadState.key === key) return loadState.promise;

	const promise = new Promise<boolean>((resolve) => {
		let settled = false;
		const finish = (ready: boolean) => {
			if (settled) return;
			settled = true;
			window.clearTimeout(timer);
			// api.js often finishes just after the timeout, so a failure is not cached.
			if (!ready && loadState?.key === key) loadState = undefined;
			resolve(ready);
		};

		const timer = window.setTimeout(() => finish(false), RECAPTCHA_READY_TIMEOUT_MS);

		// `grecaptcha` can be a partial stub before `ready`/`execute` are wired up.
		const settleWhenReady = () => {
			const grecaptcha = window.grecaptcha;
			if (grecaptcha && typeof grecaptcha.ready === 'function') {
				grecaptcha.ready(() => finish(typeof window.grecaptcha?.execute === 'function'));
			} else {
				finish(false);
			}
		};

		// An SSR-injected tag, a sibling app, or a standalone-embed host.
		if (window.grecaptcha) {
			settleWhenReady();
			return;
		}

		const existing = document.querySelector<HTMLScriptElement>('script[src*="recaptcha/api.js"]');
		if (existing) {
			existing.addEventListener('load', settleWhenReady, { once: true });
			existing.addEventListener('error', () => finish(false), { once: true });
			return;
		}

		const script = document.createElement('script');
		script.id = RECAPTCHA_SCRIPT_ID;
		script.src = `https://www.google.com/recaptcha/api.js?render=${key}`;
		script.defer = true;
		script.addEventListener('load', settleWhenReady, { once: true });
		script.addEventListener('error', () => finish(false), { once: true });
		document.head.appendChild(script);
	});

	loadState = { key, promise };
	return promise;
}

export async function generateCaptchaTokenResult(siteKey?: string): Promise<GenerateCaptchaTokenResult> {
	if (typeof window === 'undefined') return { data: null, error: 'CAPTCHA_NOT_LOADED' };

	const key = resolveSiteKey(siteKey);
	if (key === undefined) return { data: null, error: 'INVALID_CAPTCHA_KEY_OR_ORIGIN' };

	// Self-loading, so the donate flow does not depend on a form having mounted.
	const ready = await loadRecaptcha(key);
	const grecaptcha = window.grecaptcha;
	if (!ready || !grecaptcha || typeof grecaptcha.execute !== 'function') {
		return { data: null, error: 'CAPTCHA_NOT_LOADED' };
	}

	return new Promise<GenerateCaptchaTokenResult>((resolve) => {
		grecaptcha.ready(() => {
			grecaptcha
				.execute(key, { action: 'submit' })
				.then((captchaToken) => {
					resolve(captchaToken ? { data: captchaToken, error: null } : { data: null, error: 'INVALID_CAPTCHA' });
				})
				// Rejects when the key is not registered for this origin.
				.catch(() => resolve({ data: null, error: 'INVALID_CAPTCHA' }));
		});
	});
}
